import { Application, filters, Point, Renderer as PixiRenderer, utils, } from 'pixi.js-legacy';
import { fromEvent } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { Angle, AngleOptions, Beam, BeamOptions, Column, ColumnOptions, Cutout, CutoutOptions, Decking, DeckingOptions, Floor, Joist, JoistOptions, Tier, TierOptions, } from './components';
import Constants from './constants';
import { DragManager, ScalableTextManager, SelectManager, TooltipManager, UICallbacks, UIManager, } from './managers';
import Textures from './textures';
import ViewStyle from './viewStyle';
export class RendererOptions {
    static getDefaults() {
        return {
            element: document.body,
            allowTooltips: true,
            allowSelection: true,
            allowDrag: true,
            allowCutoutSnapping: true,
            allowEdgeSnapping: true,
            allowCutoutCollision: true,
            renderCutoutOutline: false,
            showCentreButton: false,
            ready: () => undefined,
            componentDeselect: () => undefined,
            componentDrag: () => undefined,
            componentDragStart: () => undefined,
            componentDragStop: () => undefined,
            componentSelect: () => undefined,
            onCenterClick: () => undefined,
        };
    }
}
export class Renderer {
    constructor() {
        this.minimumZoom = 0.01;
        this.maximumZoom = 15;
        utils.skipHello();
        this.resize = this.resize.bind(this);
    }
    getCurrentZoomLevel() {
        return this.floorContainer.scale;
    }
    init(rendererOptions) {
        this.options = Object.assign(new RendererOptions(), RendererOptions.getDefaults(), rendererOptions);
        this.app = new Application({
            width: 1600,
            height: 900,
            antialias: true,
            resolution: 2,
            autoDensity: true,
            backgroundColor: Constants.rendererBackgroundColour,
        });
        this.app.stage.sortableChildren = true;
        this.options.element.appendChild(this.app.view);
        this.element = this.options.element;
        this.allowTooltips = this.options.allowTooltips;
        this.allowSelection = this.options.allowSelection;
        this.allowDrag = this.options.allowDrag;
        this.allowCutoutSnapping = this.options.allowCutoutSnapping;
        this.allowEdgeSnapping = this.options.allowEdgeSnapping;
        this.allowCutoutCollision = this.options.allowCutoutCollision;
        this.renderCutoutOutline = this.options.renderCutoutOutline;
        this.componentDrag = this.options.componentDrag;
        this.componentSelect = this.options.componentSelect;
        this.componentDeselect = this.options.componentDeselect;
        this.componentDragStart = this.options.componentDragStart;
        this.componentDragStop = this.options.componentDragStop;
        this.onCenterClick = this.options.onCenterClick;
        this.ready = this.options.ready;
        Textures.getInstance().LoadTextures(this.app.renderer);
        SelectManager.getInstance().globalSelectCallback = this.componentSelect;
        SelectManager.getInstance().globalDeselectCallback = this.componentDeselect;
        SelectManager.getInstance().allowSelect = this.allowSelection;
        DragManager.getInstance().globalDragCallback = this.componentDrag;
        DragManager.getInstance().globalDragStartCallback = this.componentDragStart;
        DragManager.getInstance().globalDragStopCallback = this.componentDragStop;
        DragManager.getInstance().allowDrag = this.allowDrag;
        this.floorContainer = new Floor();
        TooltipManager.getInstance().container = this.floorContainer;
        TooltipManager.getInstance().allowTooltips = this.allowTooltips;
        ScalableTextManager.getInstance().container = this.floorContainer;
        this.resizeSubscription = fromEvent(window, 'resize')
            .pipe(tap(() => {
            if (this.app.renderer instanceof PixiRenderer) {
                const blurFilter = new filters.BlurFilter(10);
                this.floorContainer.filters = [blurFilter];
            }
        }), debounceTime(500), tap(() => {
            this.resize();
            UIManager.getInstance().doResize();
        }))
            .subscribe(() => {
            this.floorContainer.filters = null;
        });
        this.resize();
        this.setupUI();
        this.ready();
    }
    loadPolyline(xDim, yDim, numTiers) {
        this.floorContainer = new Floor();
        TooltipManager.getInstance().container = this.floorContainer;
        ScalableTextManager.getInstance().container = this.floorContainer;
        SelectManager.getInstance().setContainer(this.floorContainer);
        for (let i = 0; i < numTiers; i++) {
            const tierOptions = new TierOptions();
            tierOptions.number = i + 1;
            tierOptions.xDim = xDim * Constants.scalingFactor;
            tierOptions.yDim = yDim * Constants.scalingFactor;
            const tier = new Tier(tierOptions);
            const deckingOptions = new DeckingOptions();
            deckingOptions.xDim = xDim * Constants.scalingFactor;
            deckingOptions.yDim = yDim * Constants.scalingFactor;
            tier.addDecking(new Decking(deckingOptions));
            this.floorContainer.addTier(tier);
        }
        this.floorContainer.setCutoutSnapping(this.allowCutoutSnapping);
        this.floorContainer.setEdgeSnapping(this.allowEdgeSnapping);
        this.floorContainer.setCutoutCollision(this.allowCutoutCollision);
        this.floorContainer.setRenderCutoutOutline(this.renderCutoutOutline);
        this.app.stage.addChild(this.floorContainer);
        this.centerFloor();
    }
    loadFloor(floorData, viewStyle = ViewStyle.Diagram) {
        this.floorContainer = new Floor();
        TooltipManager.getInstance().container = this.floorContainer;
        ScalableTextManager.getInstance().container = this.floorContainer;
        SelectManager.getInstance().setContainer(this.floorContainer);
        for (const tierData of floorData.Floor.Tiers) {
            const tierOptions = {
                id: tierData.Id,
                number: tierData.Number,
                xDim: tierData.Size.XDim * Constants.scalingFactor,
                yDim: tierData.Size.YDim * Constants.scalingFactor,
                viewStyle: viewStyle,
            };
            const tier = new Tier(tierOptions);
            this.floorContainer.addTier(tier);
            const deckingOptions = new DeckingOptions();
            deckingOptions.xDim = tierData.Size.XDim * Constants.scalingFactor;
            deckingOptions.yDim = tierData.Size.YDim * Constants.scalingFactor;
            tier.addDecking(new Decking(deckingOptions));
            let beamDirection = 0;
            for (const beamData of tierData.Beams) {
                beamDirection = beamData.Direction;
                const beamOptions = new BeamOptions();
                beamOptions.id = beamData.Id;
                beamOptions.tier = tier;
                beamOptions.constructionType = beamData.ConstructionType;
                beamOptions.chosenSection = beamData.ChosenSection;
                beamOptions.direction = beamData.Direction;
                beamOptions.startPosition = new Point(beamData.Start.X * Constants.scalingFactor, beamData.Start.Y * Constants.scalingFactor);
                beamOptions.endPosition = new Point(beamData.End.X * Constants.scalingFactor, beamData.End.Y * Constants.scalingFactor);
                beamOptions.mark = beamData.Number;
                beamOptions.viewStyle = viewStyle;
                tier.addBeam(new Beam(this.app.renderer, beamOptions));
            }
            for (const columnData of tierData.Columns) {
                const columnOptions = new ColumnOptions();
                columnOptions.id = columnData.Id;
                columnOptions.tier = tier;
                columnOptions.chosenSection = columnData.ChosenSection;
                columnOptions.position = new Point(columnData.Position.X * Constants.scalingFactor, columnData.Position.Y * Constants.scalingFactor);
                columnOptions.mark = columnData.Number;
                columnOptions.viewStyle = viewStyle;
                tier.addColumn(new Column(columnOptions));
            }
            for (const angleData of tierData.Angles) {
                const angleOptions = new AngleOptions();
                angleOptions.id = angleData.Id;
                angleOptions.tier = tier;
                angleOptions.direction = angleData.Direction;
                angleOptions.startPosition = new Point(angleData.Start.X * Constants.scalingFactor, angleData.Start.Y * Constants.scalingFactor);
                angleOptions.endPosition = new Point(angleData.End.X * Constants.scalingFactor, angleData.End.Y * Constants.scalingFactor);
                angleOptions.mark = angleData.Number;
                angleOptions.viewStyle = viewStyle;
                tier.addAngle(new Angle(this.app.renderer, angleOptions));
            }
            for (const joistData of tierData.Joists) {
                const joistOptions = new JoistOptions();
                joistOptions.id = joistData.Id;
                joistOptions.chosenSection = joistData.ChosenSection;
                joistOptions.beamDirection = beamDirection;
                joistOptions.startPosition = new Point(joistData.Start.X * Constants.scalingFactor, joistData.Start.Y * Constants.scalingFactor);
                joistOptions.endPosition = new Point(joistData.End.X * Constants.scalingFactor, joistData.End.Y * Constants.scalingFactor);
                tier.addJoist(new Joist(joistOptions));
            }
            for (const cutoutData of tierData.Cutouts) {
                const cutoutOptions = new CutoutOptions();
                cutoutOptions.id = cutoutData.Hash;
                cutoutOptions.tier = tier;
                cutoutOptions.xDim = cutoutData.Size.XDim * Constants.scalingFactor;
                cutoutOptions.yDim = cutoutData.Size.YDim * Constants.scalingFactor;
                cutoutOptions.position = new Point(cutoutData.Position.X * Constants.scalingFactor, cutoutData.Position.Y * Constants.scalingFactor);
                tier.addCutout(new Cutout(cutoutOptions));
            }
        }
        this.floorContainer.setCutoutSnapping(this.allowCutoutSnapping);
        this.floorContainer.setEdgeSnapping(this.allowEdgeSnapping);
        this.floorContainer.setCutoutCollision(this.allowCutoutCollision);
        this.floorContainer.setRenderCutoutOutline(this.renderCutoutOutline);
        this.app.stage.addChild(this.floorContainer);
        this.centerFloor();
    }
    setupUI() {
        UIManager.getInstance().init(this.app, this.options.showCentreButton);
        UIManager.getInstance().subscribe(UICallbacks.CenterButtonPress, () => {
            this.centerFloor();
            this.onCenterClick();
        });
    }
    setZoomLevel(amount) {
        this.floorContainer.scale.set(amount);
        ScalableTextManager.getInstance().update();
        this.floorContainer.position.x =
            this.app.renderer.width / this.app.renderer.resolution / 2 -
                this.getFloorContainerWidth() / 2;
        this.floorContainer.position.y =
            this.app.renderer.height / this.app.renderer.resolution / 2 -
                this.getFloorContainerHeight() / 2;
    }
    calculateBestScale() {
        let scale = 1;
        const maxWidth = this.element.offsetWidth - 150;
        const maxHeight = this.element.offsetHeight - 150;
        const width = this.getFloorContainerWidth();
        const height = this.getFloorContainerHeight();
        while (scale > 0.05) {
            scale = scale - 0.01;
            if (width * scale < maxWidth && height * scale < maxHeight) {
                break;
            }
        }
        this.minimumZoom = scale;
        return scale;
    }
    getFloorContainerWidth() {
        return this.floorContainer.getBounds().width;
    }
    getFloorContainerHeight() {
        return this.floorContainer.getBounds().height;
    }
    centerFloor() {
        this.floorContainer.pivot.set(0, 0);
        this.setZoomLevel(this.calculateBestScale());
    }
    resize() {
        this.app.renderer.resize(this.element.offsetWidth, window.innerHeight - 300);
        this.centerFloor();
    }
    designMode() {
        this.floorContainer.designMode();
    }
    diagramMode() {
        this.floorContainer.diagramMode();
    }
    addCutout(tierNumber, cutoutData) {
        this.floorContainer.addCutout(tierNumber, cutoutData);
        this.floorContainer.setCutoutSnapping(this.allowCutoutSnapping);
        this.floorContainer.setEdgeSnapping(this.allowEdgeSnapping);
        this.floorContainer.setCutoutCollision(this.allowCutoutCollision);
        this.floorContainer.setRenderCutoutOutline(this.renderCutoutOutline);
    }
    removeCutout(id, tierNumber) {
        this.floorContainer.removeCutout(id, tierNumber);
    }
    deselectComponent(component) {
        SelectManager.getInstance().deselect(component, false);
    }
    getImageUrl() {
        this.app.renderer.render(this.app.stage);
        return this.app.renderer.view.toDataURL('image/png');
    }
    toggleCutoutSnapping() {
        this.allowCutoutSnapping = !this.allowCutoutSnapping;
        this.floorContainer.setCutoutSnapping(this.allowCutoutSnapping);
    }
    toggleEdgeSnapping() {
        this.allowEdgeSnapping = !this.allowEdgeSnapping;
        this.floorContainer.setEdgeSnapping(this.allowEdgeSnapping);
    }
    toggleCutoutCollision() {
        this.allowCutoutCollision = !this.allowCutoutCollision;
        this.floorContainer.setCutoutCollision(this.allowCutoutCollision);
    }
    toggleRenderCutoutOutline() {
        this.renderCutoutOutline = !this.renderCutoutOutline;
        this.floorContainer.setRenderCutoutOutline(this.renderCutoutOutline);
    }
    clear() {
        if (!this.floorContainer) {
            return;
        }
        ScalableTextManager.getInstance().reset();
        this.app.stage.removeChild(this.floorContainer);
        this.floorContainer.destroy({
            children: true,
            texture: true,
            baseTexture: true,
        });
        delete this.floorContainer;
    }
    destroy() {
        this.clear();
        if (this.resizeSubscription) {
            this.resizeSubscription.unsubscribe();
        }
        Textures.getInstance().destroy();
        SelectManager.getInstance().destroy();
        DragManager.getInstance().destroy();
        TooltipManager.getInstance().destroy();
        ScalableTextManager.getInstance().destroy();
        UIManager.getInstance().destroy();
        if (this.app) {
            this.app.destroy(true, {
                children: true,
                texture: true,
                baseTexture: true,
            });
        }
        delete this.app;
        delete this.options;
        delete this.element;
    }
}
